
import { Suspense, lazy } from 'react';
import { Toaster } from '@/components/ui/toaster';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import { MobilePerformanceProvider } from '@/components/MobilePerformanceProvider';
import { GameProvider } from '@/contexts/GameContext';
import { LanguageProvider, useLanguage } from '@/contexts/LanguageContext';
import { useArcadeSpotCleanup } from '@/hooks/useArcadeSpotCleanup';
import { useSecurityLogger } from '@/hooks/useSecurityLogger';
import { LanguageSyncManager } from '@/components/LanguageSyncManager';
import { URLRedirectManager } from '@/components/URLRedirectManager';
import { URLSanitizationMiddleware } from '@/components/URLSanitizationMiddleware';
import { LazyLoadingPlaceholder } from '@/components/LazyLoadingPlaceholder';
import { CriticalImagePreloader } from '@/components/CriticalImagePreloader';
import Index from '@/pages/Index';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { enhanceMTCaptchaAccessibility } from '@/utils/mtcaptchaAccessibility';
import { enableAutoImageOptimization } from '@/utils/aggressiveImageOptimizer';
import { optimizeResourceLoading } from '@/utils/deferNonCriticalCSS';

// Composant pour scroll en haut à chaque navigation
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
}

// Redirige les anciennes URLs /fr/* vers leurs equivalents canoniques francais (sans prefixe)
function LegacyFrRedirect() {
  const location = useLocation();
  const target = (location.pathname.replace(/^\/fr(?=\/|$)/, '') || '/') + location.search + location.hash;
  return <Navigate to={target} replace />;
}

// Lazy load components
const Profile = lazy(() => import('@/pages/Profile'));
const AddServer = lazy(() => import('@/pages/AddServer'));
const ServerDetail = lazy(() => import('@/pages/ServerDetail'));
const Favorites = lazy(() => import('@/pages/Favorites'));
const Leaderboard = lazy(() => import('@/pages/Leaderboard'));
const MyVotes = lazy(() => import('@/pages/MyVotes'));
const NotFound = lazy(() => import('@/pages/NotFound'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));
const PrivacyPolicy = lazy(() => import('@/pages/PrivacyPolicyLocalized'));
const TermsOfService = lazy(() => import('@/pages/TermsOfServiceLocalized'));
const TermsOfSale = lazy(() => import('@/pages/TermsOfSaleLocalized'));
const AdminTagProposals = lazy(() => import('@/pages/AdminTagProposals'));
const AdminGameProposals = lazy(() => import('@/pages/AdminGameProposals'));
const AdminPanel = lazy(() => import('@/pages/AdminPanel'));
const AdminTags = lazy(() => import('@/pages/AdminTags'));
const AdminGames = lazy(() => import('@/pages/AdminGames'));
const AdminPrestigePricing = lazy(() => import('@/pages/AdminPrestigePricing'));
const AdminPrestigePayments = lazy(() => import('@/pages/AdminPrestigePayments'));
const AdminUsers = lazy(() => import('@/pages/AdminUsers'));
const AdminModeration = lazy(() => import('@/pages/AdminModeration'));
const AdminStats = lazy(() => import('@/pages/AdminStats'));
const AdminReports = lazy(() => import('@/pages/AdminReports'));
const AdminSettings = lazy(() => import('@/pages/AdminSettings'));
const AdminSocialMedia = lazy(() => import('@/pages/AdminSocialMedia'));
const AdminSecurity = lazy(() => import('@/pages/AdminSecurity'));
const AdminArcadeSpots = lazy(() => import('@/pages/AdminArcadeSpots'));
const AdminSkySpots = lazy(() => import('@/pages/AdminVerticalPanels'));
const AdminBanners728x90 = lazy(() => import('@/pages/AdminBanners728x90'));
const AdminWebhooks = lazy(() => import('@/pages/AdminWebhooks'));
const AdminEventTypes = lazy(() => import('@/pages/AdminEventTypes'));
const AdminAnnouncement = lazy(() => import('@/pages/AdminAnnouncement'));
const GameCategory = lazy(() => import('@/pages/GameCategory'));
const ArcadeSpotSuccess = lazy(() => import('@/pages/ArcadeSpotSuccess'));
const PrestigeSuccess = lazy(() => import('@/pages/PrestigeSuccess'));
const APIDocs = lazy(() => import('@/pages/APIDocs'));
const VotifierGuide = lazy(() => import('@/pages/VotifierGuide'));
const AdminServersManagement = lazy(() => import('@/pages/AdminServersManagement'));
const AdminArenaImport = lazy(() => import('@/pages/AdminArenaImport'));
const SEOAudit = lazy(() => import('@/pages/SEOAudit'));
const ArcadeSpotsManagement = lazy(() => import('@/pages/ArcadeSpotsManagement'));
const AdminPartnersBacklinks = lazy(() => import('@/pages/AdminPartnersBacklinks'));
const AdminCompetitiveEvents = lazy(() => import('@/pages/AdminCompetitiveEvents'));
const CreatorTools = lazy(() => import('@/pages/CreatorTools'));
const AllGamesListing = lazy(() => import('@/pages/AllGamesListing'));
const AllServersListing = lazy(() => import('@/pages/AllServersListing'));
const GonePage = lazy(() => import('@/pages/GonePage'));
const SEOHealthDashboard = lazy(() => import('@/pages/SEOHealthDashboard'));
const DiscordBot = lazy(() => import('@/pages/DiscordBot'));
const AdminLicenses = lazy(() => import('@/pages/AdminLicenses'));
const AdminAppVersions = lazy(() => import('@/pages/AdminAppVersions'));
const AdminDiscordLogs = lazy(() => import('@/pages/AdminDiscordLogs'));
const AdminDiscordTemplates = lazy(() => import('@/pages/AdminDiscordTemplates'));
const AdminLicensePurchases = lazy(() => import('@/pages/AdminLicensePurchases'));
const AdminPaymentMethods = lazy(() => import('@/pages/AdminPaymentMethods'));
const AdminLicensePricing = lazy(() => import('@/pages/AdminLicensePricing'));
const AdminDiscordTickets = lazy(() => import('@/pages/AdminDiscordTickets'));
const AdminTestStripe = lazy(() => import('@/pages/AdminTestStripe'));
const AdminSeoDashboard = lazy(() => import('@/pages/AdminSeoDashboard'));
const AdminSeoRoutine = lazy(() => import('@/pages/AdminSeoRoutine'));
const AdminVoteDebug = lazy(() => import('@/pages/AdminVoteDebug'));
const SpotlightPage = lazy(() => import('@/pages/SpotlightPage'));
const SpotlightArchive = lazy(() => import('@/pages/SpotlightArchive'));
const AdminVmSettings = lazy(() => import('@/pages/AdminVmSettings'));
const AdminAutoBoost = lazy(() => import('@/pages/AdminAutoBoost'));
const queryClient = new QueryClient();

function LoadingSpinner() {
  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center">
      <div className="relative">
        <LazyLoadingPlaceholder 
          height="400px" 
          width="400px" 
          className="rounded-lg"
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gaming-primary"></div>
        </div>
      </div>
    </div>
  );
}

// Composant interne pour gérer le html lang dynamique
function AppContent() {
  const { language } = useLanguage();
  useArcadeSpotCleanup();
  useSecurityLogger(); // Activer le logging de sécurité global
  
  // Mettre à jour dynamiquement l'attribut lang du HTML
  useEffect(() => {
    const langMap: Record<string, string> = {
      'fr': 'fr',
      'en': 'en',
      'es': 'es'
    };
    document.documentElement.lang = langMap[language] || 'fr';
  }, [language]);
  
  // Améliorer l'accessibilité de MTCaptcha
  useEffect(() => {
    const cleanup = enhanceMTCaptchaAccessibility();
    return cleanup;
  }, []);
  
  // Initialiser le monitoring des Web Vitals
  useEffect(() => {
    if (typeof window !== 'undefined') {
      import('@/utils/webVitalsReporter').then(({ reportWebVitals }) => {
        reportWebVitals();
      });
    }
  }, []);
  
  // Activer l'optimisation automatique des images
  useEffect(() => {
    const cleanup = enableAutoImageOptimization();
    return cleanup;
  }, []);
  
  // Optimiser le chargement des ressources
  useEffect(() => {
    optimizeResourceLoading();
  }, []);
  
  return (
    <Router>
      <ScrollToTop />
      <CriticalImagePreloader />
      <LanguageSyncManager />
      <URLRedirectManager />
      <URLSanitizationMiddleware />
      <div className="min-h-screen bg-slate-950">
              <Suspense fallback={<LoadingSpinner />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/fr" element={<LegacyFrRedirect />} />
                <Route path="/en" element={<Index />} />
                <Route path="/es" element={<Index />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/fr/profile" element={<LegacyFrRedirect />} />
                <Route path="/en/profile" element={<Profile />} />
                <Route path="/es/profile" element={<Profile />} />
                <Route path="/add-server" element={<AddServer />} />
                <Route path="/fr/add-server" element={<LegacyFrRedirect />} />
                <Route path="/en/add-server" element={<AddServer />} />
                <Route path="/es/add-server" element={<AddServer />} />
                <Route path="/server/:id" element={<ServerDetail />} />
                <Route path="/serveur/:slug" element={<ServerDetail />} />
                <Route path="/fr/serveur/:slug" element={<LegacyFrRedirect />} />
                <Route path="/en/server/:slug" element={<ServerDetail />} />
                <Route path="/es/servidor/:slug" element={<ServerDetail />} />
                <Route path="/favorites" element={<Favorites />} />
                <Route path="/fr/favorites" element={<LegacyFrRedirect />} />
                <Route path="/en/favorites" element={<Favorites />} />
                <Route path="/es/favorites" element={<Favorites />} />
                <Route path="/leaderboard" element={<Leaderboard />} />
                <Route path="/fr/leaderboard" element={<LegacyFrRedirect />} />
                <Route path="/en/leaderboard" element={<Leaderboard />} />
                <Route path="/es/leaderboard" element={<Leaderboard />} />
                <Route path="/my-votes" element={<MyVotes />} />
                <Route path="/fr/my-votes" element={<LegacyFrRedirect />} />
                <Route path="/en/my-votes" element={<MyVotes />} />
                <Route path="/es/my-votes" element={<MyVotes />} />
                {/* Routes principales /game/:gameSlug */}
                <Route path="/game/:gameSlug" element={<GameCategory />} />
                <Route path="/fr/game/:gameSlug" element={<LegacyFrRedirect />} />
                <Route path="/en/game/:gameSlug" element={<GameCategory />} />
                <Route path="/es/game/:gameSlug" element={<GameCategory />} />
                
                {/* Pages listing de tous les jeux */}
                <Route path="/game" element={<AllGamesListing />} />
                <Route path="/fr/game" element={<LegacyFrRedirect />} />
                <Route path="/en/game" element={<AllGamesListing />} />
                <Route path="/es/game" element={<AllGamesListing />} />
                
                {/* Pages listing de tous les serveurs */}
                <Route path="/serveur" element={<AllServersListing />} />
                <Route path="/fr/serveur" element={<LegacyFrRedirect />} />
                <Route path="/en/server" element={<AllServersListing />} />
                <Route path="/es/servidor" element={<AllServersListing />} />
                
                {/* ALIAS SEO : /games/:gameSlug redirige vers /game/:gameSlug (côté client, en backup des 301 serveur) */}
                <Route path="/games/:gameSlug" element={<GameCategory />} />
                <Route path="/fr/games/:gameSlug" element={<LegacyFrRedirect />} />
                <Route path="/en/games/:gameSlug" element={<GameCategory />} />
                <Route path="/es/games/:gameSlug" element={<GameCategory />} />
                {/* Routes SEO optimisées pour chaque jeu */}
                <Route path="/serveurs-minecraft" element={<GameCategory />} />
                <Route path="/serveurs-dofus" element={<GameCategory />} />
                <Route path="/serveurs-wow" element={<GameCategory />} />
                <Route path="/serveurs-cs2" element={<GameCategory />} />
                <Route path="/serveurs-gta-rp" element={<GameCategory />} />
                <Route path="/serveurs-rust" element={<GameCategory />} />
                <Route path="/serveurs-garry-mod" element={<GameCategory />} />
                <Route path="/serveurs-dayz" element={<GameCategory />} />
                <Route path="/fr/serveurs-minecraft" element={<LegacyFrRedirect />} />
                <Route path="/fr/serveurs-dofus" element={<LegacyFrRedirect />} />
                <Route path="/fr/serveurs-wow" element={<LegacyFrRedirect />} />
                <Route path="/fr/serveurs-cs2" element={<LegacyFrRedirect />} />
                <Route path="/fr/serveurs-gta-rp" element={<LegacyFrRedirect />} />
                <Route path="/fr/serveurs-rust" element={<LegacyFrRedirect />} />
                <Route path="/fr/serveurs-garry-mod" element={<LegacyFrRedirect />} />
                <Route path="/fr/serveurs-dayz" element={<LegacyFrRedirect />} />
                <Route path="/en/servers-minecraft" element={<GameCategory />} />
                <Route path="/en/servers-dofus" element={<GameCategory />} />
                <Route path="/en/servers-wow" element={<GameCategory />} />
                <Route path="/en/servers-cs2" element={<GameCategory />} />
                <Route path="/en/servers-gta-rp" element={<GameCategory />} />
                <Route path="/en/servers-rust" element={<GameCategory />} />
                <Route path="/en/servers-garry-mod" element={<GameCategory />} />
                <Route path="/en/servers-dayz" element={<GameCategory />} />
                <Route path="/es/servidores-minecraft" element={<GameCategory />} />
                <Route path="/es/servidores-dofus" element={<GameCategory />} />
                <Route path="/es/servidores-wow" element={<GameCategory />} />
                <Route path="/es/servidores-cs2" element={<GameCategory />} />
                <Route path="/es/servidores-gta-rp" element={<GameCategory />} />
                <Route path="/es/servidores-rust" element={<GameCategory />} />
                <Route path="/es/servidores-garry-mod" element={<GameCategory />} />
                <Route path="/es/servidores-dayz" element={<GameCategory />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/fr/reset-password" element={<LegacyFrRedirect />} />
                <Route path="/en/reset-password" element={<ResetPassword />} />
                <Route path="/es/reset-password" element={<ResetPassword />} />
                <Route path="/admin" element={<AdminPanel />} />
                <Route path="/admin/tag-proposals" element={<AdminTagProposals />} />
                <Route path="/admin/game-proposals" element={<AdminGameProposals />} />
                <Route path="/admin/tags" element={<AdminTags />} />
                <Route path="/admin/games" element={<AdminGames />} />
                <Route path="/admin/prestige-pricing" element={<AdminPrestigePricing />} />
        <Route path="/admin/prestige-payments" element={<AdminPrestigePayments />} />
        <Route path="/admin/users" element={<AdminUsers />} />
        <Route path="/admin/moderation" element={<AdminModeration />} />
        <Route path="/admin/stats" element={<AdminStats />} />
        <Route path="/admin/reports" element={<AdminReports />} />
        <Route path="/admin/settings" element={<AdminSettings />} />
                <Route path="/admin/social-media" element={<AdminSocialMedia />} />
                <Route path="/admin/security" element={<AdminSecurity />} />
                <Route path="/admin/arcade-spots" element={<AdminArcadeSpots />} />
                <Route path="/admin/sky-spots" element={<AdminSkySpots />} />
                <Route path="/admin/event-types" element={<AdminEventTypes />} />
                <Route path="/admin/webhooks/:serverId" element={<AdminWebhooks />} />
                <Route path="/admin/arcade-spots/pricing" element={<AdminArcadeSpots />} />
                <Route path="/admin/servers" element={<AdminServersManagement />} />
                <Route path="/admin/arena-import" element={<AdminArenaImport />} />
                <Route path="/admin/partners-backlinks" element={<AdminPartnersBacklinks />} />
                <Route path="/admin/competitive-events" element={<AdminCompetitiveEvents />} />
                <Route path="/admin/seo-health" element={<SEOHealthDashboard />} />
                <Route path="/admin/banners-728x90" element={<AdminBanners728x90 />} />
                <Route path="/admin/announcement" element={<AdminAnnouncement />} />
                <Route path="/admin/licenses" element={<AdminLicenses />} />
                <Route path="/admin/app-versions" element={<AdminAppVersions />} />
                <Route path="/admin/discord-logs" element={<AdminDiscordLogs />} />
                <Route path="/admin/discord-templates" element={<AdminDiscordTemplates />} />
                <Route path="/admin/license-purchases" element={<AdminLicensePurchases />} />
                <Route path="/admin/seo-dashboard" element={<AdminSeoDashboard />} />
                <Route path="/admin/seo-routine" element={<AdminSeoRoutine />} />
                <Route path="/:lang/spotlight/:date/:slug" element={<SpotlightPage />} />
                <Route path="/spotlight/:date/:slug" element={<SpotlightPage />} />
                <Route path="/:lang/spotlight" element={<SpotlightArchive />} />
                <Route path="/spotlight" element={<SpotlightArchive />} />
                <Route path="/admin/vote-debug" element={<AdminVoteDebug />} />
                <Route path="/admin/auto-boost" element={<AdminAutoBoost />} />
                <Route path="/admin/vm-settings" element={<AdminVmSettings />} />
                <Route path="/admin/payment-methods" element={<AdminPaymentMethods />} />
                <Route path="/admin/license-pricing" element={<AdminLicensePricing />} />
                <Route path="/admin/discord-tickets" element={<AdminDiscordTickets />} />
                <Route path="/admin/test-stripe" element={<AdminTestStripe />} />
                <Route path="/arcade-spots/success" element={<ArcadeSpotSuccess />} />
                <Route path="/prestige/success" element={<PrestigeSuccess />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/fr/privacy" element={<LegacyFrRedirect />} />
                <Route path="/en/privacy" element={<PrivacyPolicy />} />
                <Route path="/es/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/fr/terms" element={<LegacyFrRedirect />} />
                <Route path="/en/terms" element={<TermsOfService />} />
                <Route path="/es/terms" element={<TermsOfService />} />
                <Route path="/terms-of-sale" element={<TermsOfSale />} />
                <Route path="/fr/terms-of-sale" element={<LegacyFrRedirect />} />
                <Route path="/en/terms-of-sale" element={<TermsOfSale />} />
                <Route path="/es/terms-of-sale" element={<TermsOfSale />} />
                <Route path="/api-docs" element={<APIDocs />} />
                <Route path="/fr/api-docs" element={<LegacyFrRedirect />} />
                <Route path="/en/api-docs" element={<APIDocs />} />
                <Route path="/es/api-docs" element={<APIDocs />} />
                <Route path="/guide-votifier" element={<VotifierGuide />} />
                <Route path="/fr/guide-votifier" element={<LegacyFrRedirect />} />
                <Route path="/en/votifier-guide" element={<VotifierGuide />} />
                <Route path="/es/guia-votifier" element={<VotifierGuide />} />
                <Route path="/seo-audit" element={<SEOAudit />} />
                <Route path="/arcade-spots-management" element={<ArcadeSpotsManagement />} />
                <Route path="/creator-tools" element={<CreatorTools />} />
                <Route path="/fr/creator-tools" element={<LegacyFrRedirect />} />
                <Route path="/en/creator-tools" element={<CreatorTools />} />
                <Route path="/es/creator-tools" element={<CreatorTools />} />
                <Route path="/410" element={<GonePage />} />
                <Route path="/discord-bot" element={<DiscordBot />} />
                <Route path="/fr/discord-bot" element={<LegacyFrRedirect />} />
                <Route path="/en/discord-bot" element={<DiscordBot />} />
                <Route path="/es/discord-bot" element={<DiscordBot />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
            <Toaster />
          </div>
        </Router>
  );
}

function App() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <LanguageProvider>
          <MobilePerformanceProvider>
            <GameProvider>
              <AppContent />
            </GameProvider>
          </MobilePerformanceProvider>
        </LanguageProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;
